import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuotationState } from '@tailoring-crm/shared';
import { Badge, type BadgeProps } from '@tailoring-crm/ui';

/**
 * Where one quotation stands, in a word.
 *
 * Six states, and the two the calendar decides are the reason this is a component rather than a
 * `t()` call at each site: `expired` and `superseded` are never stored, so a screen that spelled
 * out its own rules would eventually disagree with the server about which of them applies. The
 * API computes `state` on every read and this renders exactly what it was given.
 *
 * The word is never carried by the colour alone — every badge says what it is. That matters more
 * here than almost anywhere else in the product: the difference between a price that stands and
 * one that has lapsed is a difference a customer will argue about.
 */
const VARIANT_OF: Readonly<Record<QuotationState, NonNullable<BadgeProps['variant']>>> =
  Object.freeze({
    draft: 'outline',
    sent: 'default',
    accepted: 'success',
    rejected: 'destructive',
    expired: 'warning',
    // Not a failure — it is what a revision does to the version before it — so it reads as quiet
    // rather than as a problem.
    superseded: 'secondary',
  });

export function QuotationStateBadge({ state }: { state: QuotationState }): ReactElement {
  const { t } = useTranslation();
  return <Badge variant={VARIANT_OF[state]}>{t(`quotations.state.${state}`)}</Badge>;
}
